/**
 * /auth/mfa-verify — second-factor entry on sign-in.
 *
 * Hit when a user with an aal1 session needs to step up to aal2. The user
 * chooses HOW: an authenticator code, or a passkey. Both are real Supabase
 * factors and both end in the same place — a token carrying `aal: 'aal2'`.
 *
 *   TOTP    — `mfa.listFactors().totp` + `mfa.challengeAndVerify({factorId, code})`,
 *             the SDK helper that does challenge + verify in one round trip.
 *   PASSKEY — `mfa.webauthn.authenticate({factorId})` (lib/mfa-passkey.ts).
 *             Supabase runs the ceremony itself, so the resulting token carries
 *             `amr: 'mfa/webauthn'` and aal2 — a passkey holder needs no
 *             authenticator app at all. Offered only when a VERIFIED webauthn
 *             factor exists and the browser can run a ceremony; otherwise the
 *             screen is exactly the TOTP screen it always was.
 *
 * On success the session is promoted to aal2; we route BY ROLE via the shared
 * resolver — firm staff to /admin, the external auditor to /audit, the B2C
 * trustee/beneficiary to /app. (2026-06-23: this previously hard-navigated
 * everyone to /app/dashboard, stranding an admin who stepped up here on the
 * single-trust B2C dashboard.) On failure (wrong code, expired challenge, no
 * factor enrolled), surface the message inline and retry.
 *
 * ── The opt-in extra email code (Paul, 2026-08-18) ──────────────────────────
 *
 * A user who switched the extra email code on in their Security Centre is
 * challenged for an emailed code BEFORE either factor is accepted.
 *
 * The ordering is the safety argument, not a UX preference. Until `mfa.verify`
 * runs the session is still aal1, and both `/app/_layout` and `/admin/_layout`
 * bounce an aal1 session straight back to this screen — so a user who walks
 * away from the email step and types a URL lands right back on it. Demanding
 * the code AFTER the factor would have left them holding an aal2 session while
 * the challenge was still outstanding, and navigating away would have been a
 * way past the layer.
 *
 * The email code raises NOTHING by itself. It gates whether this screen will
 * accept a factor at all; the factor is still the only thing that moves the
 * session to aal2. See lib/mfa-email-code.ts.
 *
 * If the code cannot be delivered (feature unconfigured, ACS degraded, or the
 * send exceeds its timeout) the layer is SKIPPED and the user continues on the
 * factor they already hold — Paul's ruling: nobody is locked out because an
 * email was slow. The skip is announced on screen; a security control that
 * quietly stopped applying is worse than one that never existed. A WRONG code
 * is never a skip.
 */

import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { AuthSubmit } from '../../components/auth/auth-controls';
import { supabase } from '../../lib/supabase';
import { mapSupabaseAuthError } from '../../lib/auth-errors';
import { landingRouteForRole } from '../../lib/auth-landing';
import { prefetchDashboard } from '../../lib/api/prefetch-dashboard';
import { prefetchAdminDashboard } from '../../lib/api/prefetch-admin-dashboard';
import { queryClient } from '../../lib/query-client';
import {
  isPasskeyFactorSupported,
  listPasskeyFactors,
  verifyWithPasskeyFactor,
  type PasskeyFactor,
} from '../../lib/mfa-passkey';
import {
  isEmailCodeEnabled,
  sendEmailCode,
  verifyEmailCode,
} from '../../lib/mfa-email-code';
import type { UserRole } from '../../lib/api';

export const Route = createFileRoute('/auth/mfa-verify')({
  component: MfaVerifyPage,
});

/** Shared styling for the big centred code inputs (TOTP + email code). */
const codeInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '16px 18px',
  borderRadius: 9,
  border: '1px solid var(--hairline-2)',
  background: 'var(--bg)',
  color: 'var(--ink)',
  fontFamily: 'var(--mono)',
  fontSize: 32,
  letterSpacing: '0.25em',
  textAlign: 'center',
  boxSizing: 'border-box',
};

function InlineAlert({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        background: 'rgba(220, 38, 38, 0.08)',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        color: '#991B1B',
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}

/** Non-error notice — used for the "we couldn't email your code, continuing"
 *  disclosure, which must be visible but is not a failure the user caused. */
function InlineNotice({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        background: 'var(--surface-2)',
        border: '1px solid var(--hairline)',
        color: 'var(--ink-2)',
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}

/** `email-code` gates entry to `factor`; `factor` is the screen this route has
 *  always been. A user with the extra code switched off never sees the first. */
type Phase = 'email-code' | 'factor';

function MfaVerifyPage() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [passkeyFactor, setPasskeyFactor] = useState<PasskeyFactor | null>(null);
  const [factorError, setFactorError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  // Extra email code. `phase` starts on 'factor' so a user without the opt-in
  // — the overwhelming majority — sees no flicker and no extra render.
  const [phase, setPhase] = useState<Phase>('factor');
  const [emailCode, setEmailCode] = useState('');
  const [emailCodeError, setEmailCodeError] = useState<string | null>(null);
  const [emailCodeNotice, setEmailCodeNotice] = useState<string | null>(null);
  const [emailCodeBusy, setEmailCodeBusy] = useState(false);
  // StrictMode double-invokes effects; a second send would be a second email.
  const emailCodeRequested = useRef(false);

  // Cold-start ruling (2026-07-15): the user spends ~10s on this screen
  // typing their code — prefetch the b2c dashboard's primary queries NOW so
  // the landing after verify paints from cache (and the cold Function App is
  // warm by then). Guarded: session must exist, b2c landing surface only,
  // fire-and-forget (a failure changes nothing about this screen). React
  // Query dedupes against the login-page prefetch within its staleTime.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled || !data.session) return;
      const role = data.session.user?.app_metadata?.['role'] as UserRole | undefined;
      const landing = landingRouteForRole(role);
      if (landing === '/app/dashboard') {
        prefetchDashboard(queryClient);
      } else if (landing === '/admin/client-book') {
        prefetchAdminDashboard(queryClient, role);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Locate this account's factors. TOTP is the baseline path; a verified
  // passkey factor, when one exists, is offered alongside it. If NEITHER is
  // enrolled the user needs to enrol first, so we surface a helpful message
  // rather than silently failing.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      if (error) {
        setFactorError(mapSupabaseAuthError(error.message));
        return;
      }
      const verifiedTotp = data?.totp.find((f) => f.status === 'verified');
      if (verifiedTotp) setFactorId(verifiedTotp.id);

      // Passkey discovery is best-effort and must never break the TOTP screen:
      // an SDK or project without WebAuthn factors simply yields none.
      let passkey: PasskeyFactor | null = null;
      if (isPasskeyFactorSupported()) {
        try {
          passkey = (await listPasskeyFactors())[0] ?? null;
        } catch {
          passkey = null;
        }
      }
      if (cancelled) return;
      if (passkey) setPasskeyFactor(passkey);

      if (!verifiedTotp && !passkey) {
        setFactorError(
          'No authenticator is enrolled on this account. Enrol one before signing in with a code.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Extra email code — arm it BEFORE any factor is accepted (see the header).
  useEffect(() => {
    if (emailCodeRequested.current) return;
    emailCodeRequested.current = true;
    let cancelled = false;
    void (async () => {
      if (!(await isEmailCodeEnabled())) return;
      if (cancelled) return;
      setPhase('email-code');
      setEmailCodeBusy(true);
      const outcome = await sendEmailCode('sign-in');
      if (cancelled) return;
      setEmailCodeBusy(false);
      if (outcome.status === 'unavailable') {
        // Fail OPEN — the user already holds a real factor, and Paul's ruling
        // is that a slow email must never lock anyone out. Say so plainly
        // rather than dropping the control silently.
        setEmailCodeNotice(
          `${outcome.reason} You can continue with your authenticator or passkey.`,
        );
        setPhase('factor');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onEmailCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (emailCode.length !== 6 || emailCodeBusy) return;
    setEmailCodeError(null);
    setEmailCodeBusy(true);
    const outcome = await verifyEmailCode('sign-in', emailCode);
    setEmailCodeBusy(false);
    if (outcome.status === 'ok') {
      setPhase('factor');
      return;
    }
    if (outcome.status === 'unavailable') {
      setEmailCodeNotice(`${outcome.reason} You can continue with your authenticator or passkey.`);
      setPhase('factor');
      return;
    }
    // 'invalid' and 'retry' both keep the user here. Neither is ever a pass.
    setEmailCodeError(outcome.message);
  }

  async function resendEmailCode() {
    if (emailCodeBusy) return;
    setEmailCodeError(null);
    setEmailCodeBusy(true);
    const outcome = await sendEmailCode('sign-in');
    setEmailCodeBusy(false);
    if (outcome.status === 'unavailable') {
      setEmailCodeNotice(`${outcome.reason} You can continue with your authenticator or passkey.`);
      setPhase('factor');
    } else {
      setEmailCodeNotice('We’ve sent your code again. It can take a moment to arrive.');
    }
  }

  /** Shared landing hop — role-resolved so a stepped-up admin returns to the
   *  firm console rather than the B2C dashboard. */
  async function goToLanding() {
    const { data: sess } = await supabase.auth.getSession();
    const role = sess.session?.user?.app_metadata?.['role'] as UserRole | undefined;
    void navigate({ to: landingRouteForRole(role) });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId || otp.length !== 6 || verifying) return;
    setVerifyError(null);
    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: otp,
    });
    if (error) {
      setVerifying(false);
      setVerifyError(mapSupabaseAuthError(error.message));
      return;
    }
    // Route to the role's home surface — a firm admin who stepped up returns to
    // /admin/dashboard, a B2C user to /app/dashboard. (Was hardcoded to
    // /app/dashboard, which dropped a stepped-up admin on the B2C surface.)
    await goToLanding();
  }

  async function onPasskeySignIn() {
    if (!passkeyFactor || verifying) return;
    setVerifyError(null);
    setVerifying(true);
    const outcome = await verifyWithPasskeyFactor(passkeyFactor.id);
    setVerifying(false);
    if (outcome.status === 'ok') {
      await goToLanding();
      return;
    }
    // A cancelled ceremony is the user changing their mind — no error banner.
    if (outcome.status === 'cancelled') return;
    setVerifyError(mapSupabaseAuthError(outcome.message));
  }

  const canSubmit = factorId !== null && otp.length === 6 && !verifying;

  // ── Extra email code gate ────────────────────────────────────────────────
  if (phase === 'email-code') {
    return (
      <AuthLayout
        eyebrow="Extra security"
        heading="Check your email."
        sub="You switched on an extra emailed code. Enter it here, then confirm with your authenticator or passkey."
      >
        <form
          onSubmit={(e) => void onEmailCodeSubmit(e)}
          style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
              Code from your email
            </span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000 000"
              autoFocus
              autoComplete="one-time-code"
              aria-label="Emailed security code"
              className="mono"
              disabled={emailCodeBusy}
              style={codeInputStyle}
            />
          </label>

          {emailCodeError !== null && <InlineAlert>{emailCodeError}</InlineAlert>}

          <AuthSubmit
            label="Continue"
            busyLabel="Checking…"
            busy={emailCodeBusy}
            disabled={emailCode.length !== 6 || emailCodeBusy}
          />
        </form>

        <div
          style={{
            marginTop: 22,
            paddingTop: 18,
            borderTop: '1px solid var(--hairline)',
            fontSize: 13.5,
            color: 'var(--ink-3)',
            lineHeight: 1.6,
          }}
        >
          Didn’t get it?{' '}
          <button
            type="button"
            onClick={() => void resendEmailCode()}
            disabled={emailCodeBusy}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'var(--primary-600)',
              fontWeight: 500,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              cursor: emailCodeBusy ? 'wait' : 'pointer',
            }}
          >
            Send it again
          </button>
          . This code is an extra check you turned on. You will still confirm
          with your authenticator or passkey on the next screen.
        </div>
      </AuthLayout>
    );
  }

  // ── Factor entry ─────────────────────────────────────────────────────────
  return (
    <AuthLayout
      eyebrow="Two-factor authentication"
      heading="Enter your code."
      sub="Open your authenticator app and enter the current six-digit code."
    >
      {emailCodeNotice !== null && (
        <div style={{ marginBottom: 20 }}>
          <InlineNotice>{emailCodeNotice}</InlineNotice>
        </div>
      )}

      {/* Passkey branch — a real Supabase webauthn factor, so this reaches the
          SAME aal2 the code below does. Shown above the code entry because a
          user who has one should not have to hunt for it. */}
      {passkeyFactor !== null && (
        <div style={{ marginBottom: 22 }}>
          <button
            type="button"
            onClick={() => void onPasskeySignIn()}
            disabled={verifying}
            data-testid="mfa-verify-passkey"
            style={{
              width: '100%',
              padding: '14px 18px',
              borderRadius: 10,
              border: 'none',
              background: verifying ? 'var(--surface-2)' : 'var(--primary-700)',
              color: verifying ? 'var(--ink-4)' : 'var(--surface)',
              fontFamily: 'var(--sans)',
              fontSize: 14.5,
              fontWeight: 600,
              cursor: verifying ? 'wait' : 'pointer',
            }}
          >
            {verifying ? 'Waiting for your passkey…' : 'Use your passkey'}
          </button>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: 12.5,
              color: 'var(--ink-3)',
              lineHeight: 1.55,
              textAlign: 'center',
            }}
          >
            Face ID, Touch ID, Windows Hello or a security key
            {passkeyFactor.friendlyName ? `, ${passkeyFactor.friendlyName}` : ''}.
          </p>
          {factorId !== null && (
            <div
              style={{
                margin: '18px 0 0',
                textAlign: 'center',
                fontSize: 12.5,
                color: 'var(--ink-4)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              or use a code
            </div>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => void onSubmit(e)}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
            Authentication code
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000 000"
            autoFocus
            autoComplete="one-time-code"
            aria-label="One-time password"
            className="mono"
            disabled={verifying || factorId === null}
            style={codeInputStyle}
          />
        </label>

        {factorError !== null && <InlineAlert>{factorError}</InlineAlert>}

        {verifyError !== null && <InlineAlert>{verifyError}</InlineAlert>}

        <AuthSubmit label="Verify" busyLabel="Verifying…" busy={verifying} disabled={!canSubmit} />
      </form>

      <div
        style={{
          marginTop: 22,
          paddingTop: 18,
          borderTop: '1px solid var(--hairline)',
          fontSize: 13.5,
          color: 'var(--ink-3)',
          lineHeight: 1.6,
        }}
      >
        Lost your authenticator?{' '}
        <Link
          to="/auth/recovery"
          style={{ color: 'var(--primary-600)', fontWeight: 500 }}
        >
          Recover access
        </Link>
      </div>
    </AuthLayout>
  );
}
